// src/pages/TuneManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Music, FileText, Star, StarOff } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './TuneManagementPage.css';

interface Song {
  song_id: number;
  title: string;
  composer_name: string | null;
  lyricist_name: string | null;
}

interface TuneGroup {
  tune_id: number;
  tune_name: string;
  meter_id: number | null;
  meter: string | null;
  meter_description: string | null;
  is_preferred: boolean;
  song_count: number;
  songs: Song[];
}

interface TuneData {
  success: boolean;
  data: TuneGroup[];
  summary: {
    total_tunes: number;
    total_songs: number;
    preferred_tunes: number;
  };
}

interface TuneManagementPageProps {
  hideNavigation?: boolean;
}

const TuneManagementPage: React.FC<TuneManagementPageProps> = ({ hideNavigation = false }) => {
  const [tuneData, setTuneData] = useState<TuneData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTunes, setExpandedTunes] = useState<Set<number>>(new Set());
  const [filterPreferred, setFilterPreferred] = useState(false);

  useEffect(() => {
    loadTuneData();
  }, []);

  const loadTuneData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/tunes/songs');
      setTuneData(response.data);
    } catch (err) {
      console.error('Error loading tune data:', err);
      setError('Failed to load tune data');
    } finally {
      setLoading(false);
    }
  };

  const toggleTuneExpansion = (tuneId: number) => {
    setExpandedTunes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tuneId)) {
        newSet.delete(tuneId);
      } else {
        newSet.add(tuneId);
      }
      return newSet;
    });
  };

  const filteredTunes = tuneData?.data.filter(tune => 
    !filterPreferred || tune.is_preferred
  ) || [];

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/settings" className="btn btn-secondary">
          <ArrowLeft size={16} className="me-2" />
          Back to Settings
        </Link>
      </div>
    );
  }

  return (
    <>
      {!hideNavigation && <Navigation />}
      <div className="container mt-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Link to="/settings" className="btn btn-outline-secondary me-3">
          <ArrowLeft size={16} />
        </Link>
        <Music className="me-2" size={32} />
        <h1>Tune Management</h1>
      </div>

      {/* Summary */}
      {tuneData && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <Music size={24} className="text-primary mb-2" />
                <h5>{tuneData.summary.total_tunes}</h5>
                <small className="text-muted">Total Tunes</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <FileText size={24} className="text-info mb-2" />
                <h5>{tuneData.summary.total_songs}</h5>
                <small className="text-muted">Total Songs</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <Star size={24} className="text-warning mb-2" />
                <h5>{tuneData.summary.preferred_tunes}</h5>
                <small className="text-muted">Preferred Tunes</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <div className="form-check form-switch d-flex justify-content-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="filterPreferred"
                    checked={filterPreferred}
                    onChange={(e) => setFilterPreferred(e.target.checked)}
                  />
                  <label className="form-check-label ms-2" htmlFor="filterPreferred">
                    Show Only Preferred
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tune Groups */}
      <div className="tune-groups">
        {filteredTunes.map((tuneGroup) => (
          <div key={tuneGroup.tune_id} className="tune-group mb-4">
            <div className="card">
              <div 
                className="card-header tune-header"
                onClick={() => toggleTuneExpansion(tuneGroup.tune_id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    {tuneGroup.is_preferred ? (
                      <Star size={20} className="text-warning me-2" fill="currentColor" />
                    ) : (
                      <StarOff size={20} className="text-muted me-2" />
                    )}
                    <div>
                      <h5 className="mb-0">{tuneGroup.tune_name}</h5>
                      {tuneGroup.meter && (
                        <span className="text-muted small">
                          Meter: {tuneGroup.meter}
                          {tuneGroup.meter_description && ` (${tuneGroup.meter_description})`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-primary me-3">
                      {tuneGroup.song_count} {tuneGroup.song_count === 1 ? 'song' : 'songs'}
                    </span>
                    <span className="expand-icon">
                      {expandedTunes.has(tuneGroup.tune_id) ? '−' : '+'}
                    </span>
                  </div>
                </div>
              </div>
              
              {expandedTunes.has(tuneGroup.tune_id) && (
                <div className="card-body">
                  <div className="song-list">
                    {tuneGroup.songs.map((song, index) => (
                      <div key={song.song_id} className="song-item">
                        <div className="row align-items-center">
                          <div className="col-md-6">
                            <strong>{song.title}</strong>
                          </div>
                          <div className="col-md-3">
                            {song.composer_name && (
                              <div className="text-muted small">
                                Composer: {song.composer_name}
                              </div>
                            )}
                          </div>
                          <div className="col-md-2">
                            {song.lyricist_name && (
                              <div className="text-muted small">
                                Lyricist: {song.lyricist_name}
                              </div>
                            )}
                          </div>
                          <div className="col-md-1 text-end">
                            <Link 
                              to={`/songs/${song.song_id}`} 
                              className="btn btn-sm btn-outline-primary"
                            >
                              Edit
                            </Link>
                          </div>
                        </div>
                        {index < tuneGroup.songs.length - 1 && <hr className="my-2" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* No data message */}
      {filteredTunes.length === 0 && tuneData?.data.length === 0 && (
        <div className="text-center text-muted py-5">
          <Music size={48} className="mb-3" />
          <p>No tunes with songs found.</p>
        </div>
      )}

      {/* No filtered results */}
      {filteredTunes.length === 0 && tuneData?.data && tuneData.data.length > 0 && filterPreferred && (
        <div className="text-center text-muted py-5">
          <Star size={48} className="mb-3" />
          <p>No preferred tunes found.</p>
          <button 
            className="btn btn-outline-secondary"
            onClick={() => setFilterPreferred(false)}
          >
            Show All Tunes
          </button>
        </div>
      )}
      </div>
    </>
  );
};

export default TuneManagementPage;